/* eslint-disable react-dom/no-missing-iframe-sandbox */
/* eslint-disable react-hooks/exhaustive-deps */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import clsx from "clsx";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaBars } from "react-icons/fa";
import { IoWarningOutline } from "react-icons/io5";

import { useUpdateArtistAccountMeta, useUpdateMyProfile } from "../api/mutations";
import {
  useGetArtistMeta,
  useGetContracts,
  useGetMyAccount,
  useGetMyAccounts,
  useGetMyProfile,
  useGetUserMeta,
} from "../api/queries";
import useSiteConfigAuth from "../api/sanity/queries/getSiteConfigWithCode";
import { apiCall } from "../api/shared";
import AnnouncementBanner from "../components/AnnouncementBanner";
import ChooseAccountModal from "../components/ChooseAccountModal";
import LanguageSelector from "../components/LanguageSelector";
import Sidebar from "../components/Sidebar";
import UserDropDown from "../components/UserDropDown";
import currencies from "../helpers/currency";
import useGetColours from "../helpers/getColours";
import { logout, sendEvent, sendProperty } from "../helpers/helpers";
import { processKycStatuses } from "../helpers/kycStatus";
import useWindowDimensions from "../helpers/useWindowDimensions";
import { useSyncThemeStore } from "../hooks/useSyncThemeStore";
import { DevTools } from "../newComponents/devTools/DevTools";
import useAppStore, { getDefaultAccount } from "../store/appStateZustand";
import Loading from "./Loading";

function AuthLayout(props) {
  const { data: siteConfig, isSuccess: configIsSuccess } = useSiteConfigAuth();
  const selectedAccount = useAppStore(state => state.selectedAccount);
  const hubspotProps = useAppStore(state => state.hubspot);
  const kyc = useAppStore(state => state.kyc);
  const authentication = useAppStore(state => state.authentication);
  const queryClient = useQueryClient();
  // const setSelectedAccount = useAppStore(state => state.setSelectedAccount);
  // const setSelectedCatalogue = useAppStore(state => state.setSelectedCatalogue);

  // State for smooth account switching
  const isAccountSwitching = useAppStore(s => s.isAccountSwitching);
  const setAccountSwitching = useAppStore(s => s.setAccountSwitching);
  const [previousAccountData, setPreviousAccountData] = useState(null);

  // State for showing account selection modal on login
  const [showLoginAccountModal, setShowLoginAccountModal] = useState(false);

  // Check if this is a fresh login session (not a page refresh)
  // We use sessionStorage so it resets when the browser/tab is closed
  const [hasCheckedAccountOnLogin, setHasCheckedAccountOnLogin] = useState(() => {
    return sessionStorage.getItem("@hasCheckedAccountOnLogin") === "true";
  });

  // Temporary override for testing - set to true to force TuneCore mode
  const [FORCE_TUNECORE_MODE, setForceTunecoreMode] = useState(false);

  const isTunecore = FORCE_TUNECORE_MODE || siteConfig?.display_name === "TuneCore";

  // Sync theme stores - handles both local UI state and shared @sentricmusic/ui theme
  useSyncThemeStore(isTunecore, configIsSuccess && !!siteConfig);

  useEffect(() => {
    if (configIsSuccess && siteConfig) {
      useAppStore.setState({ hubspotDisabled: siteConfig?.disable_hubspot_tracking });
    }
  }, [siteConfig, configIsSuccess]);

  const authLayoutClasses = clsx("page-fade-in relative w-full antialiased", {
    "font-sentric bg-sentric-rebrand-light-grey": !isTunecore,
    "font-tunecore bg-tunecore-black text-white": isTunecore,
  });
  const { width } = useWindowDimensions();
  const flags = useFlags();
  const showOldSignUp = flags["pd-show-old-sign-up-flow"];

  const isProduction = import.meta.env.VITE_STAGE === "prd";

  // END OF CONDITIONAL CLASSES FOR REBRAND
  const { children } = props;
  const [menu, setMenu] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const navigate = useNavigate();
  const location = useRouterState({ select: s => s.location });

  // Check if user is currently on the RaAuth page (delegated login in progress)
  const isOnRaAuthPage = location.pathname === "/raauth";

  const { t } = useTranslation();
  const userMeta = useGetUserMeta();
  const updateArtistMeta = useUpdateArtistAccountMeta();
  const profile = useGetMyProfile();
  const createProfileMutation = useUpdateMyProfile();

  const myAccounts = useGetMyAccounts();

  const filteredAccounts = myAccounts.data?.artistaccounts?.filter(
    account => !siteConfig?.excluded_catalogue_codes?.includes(account.cataloguecode),
  ) || [];

  // Check if critical data is ready - simple state check
  const apiReady = userMeta.isSuccess && myAccounts.isSuccess;

  // Session validation - ensure we have a valid authentication state
  useEffect(() => {
    // If no authentication token, redirect to login
    if (!authentication?.access_token) {
      console.warn("[AuthLayout] No valid authentication token found - redirecting to login");
      navigate({ to: "/login" });
    }
  }, [authentication, navigate]); // Now properly reactive to authentication changes

  // Token expiration monitoring with rate limiting - check every 5 minutes instead of aggressive polling
  useEffect(() => {
    const authentication = useAppStore.getState().authentication;

    if (!authentication?.access_token)
      return;

    const checkTokenExpiry = () => {
      const currentAuth = useAppStore.getState().authentication;

      // If token has expires_at timestamp and it's expired by more than 2 minutes, logout
      // Add 2-minute buffer to prevent premature logouts due to timing issues
      const bufferMs = 2 * 60 * 1000; // 2 minutes buffer
      if (currentAuth?.expires_at && Date.now() > (currentAuth.expires_at + bufferMs)) {
        console.warn("[AUTH] Token expired (with buffer), logging out");
        logout();
        navigate({ to: "/login" });
      }
    };

    // Check token expiry every 5 minutes - 401s are now handled with retry logic
    const interval = setInterval(checkTokenExpiry, 5 * 60 * 1000);

    // Also check immediately
    checkTokenExpiry();

    return () => clearInterval(interval);
  }, [navigate]);

  const noAccounts = myAccounts.isSuccess
    && (myAccounts.data === null
      || myAccounts.data === undefined
      || myAccounts.data === "undefined");

  const newUser = userMeta.isSuccess
    && noAccounts
    && (userMeta.data === "undefined"
      || userMeta.data === undefined
      || userMeta.data === null);

  // Ensure selected account is valid, otherwise pick the first available account or redirect
  // This effect must run after noAccounts and newUser are calculated
  useEffect(() => {
    // Don't run until myAccounts has successfully loaded
    if (!apiReady || !myAccounts.isSuccess || !siteConfig) {
      console.log("[AuthLayout] Waiting for data to load...");
      return;
    }

    // Skip validation during onboarding/account creation flows
    const isOnboardingFlow = location.pathname.includes("onboarding")
      || location.pathname.includes("add-account");

    if (isOnboardingFlow) {
      console.log("[AuthLayout] In onboarding flow - skipping account validation");
      return;
    }

    // Skip validation for new users who haven't created accounts yet
    if (newUser || (noAccounts && !selectedAccount)) {
      console.log("[AuthLayout] New user or no accounts - letting onboarding handle navigation");
      return;
    }

    console.log("[AuthLayout] Checking account exclusions...");

    if (filteredAccounts.length === 0) {
      console.warn("[AuthLayout] No valid accounts remain. Redirecting to /login.");
      navigate({ to: "/login" });
      return;
    }

    const isSelectedAccountValid = filteredAccounts.some(
      account => account.artistaccountcode === selectedAccount,
    );

    if (!isSelectedAccountValid) {
      console.warn(
        `[AuthLayout] Selected account (${selectedAccount}) is excluded. Switching to first available account: ${filteredAccounts[0]?.artistaccountcode}`,
      );

      // Update state without reloading - let React Query handle cache invalidation
      useAppStore.setState({
        selectedAccount: filteredAccounts[0]?.artistaccountcode,
        selectedCatalogue: filteredAccounts[0]?.cataloguecode,
      });

      // Invalidate queries that depend on selectedAccount to ensure fresh data
      queryClient.invalidateQueries({
        predicate: (query) => {
          // Invalidate queries that use account-specific data
          return query.queryKey.some(key =>
            typeof key === "string"
            && (key.includes("Account") || key.includes("artistaccount") || key.includes("aac")),
          );
        },
      });
    }
  }, [filteredAccounts, selectedAccount, navigate, myAccounts.isSuccess, siteConfig, apiReady, queryClient, newUser, noAccounts, location.pathname]);

  const { darkColour, highlightColour } = useGetColours();

  const myAccount = useGetMyAccount({
    artistaccountcode: selectedAccount,
  });

  const artistMeta = useGetArtistMeta({
    aac: selectedAccount,
  });

  // Store previous account data when switching accounts for smooth UX
  useEffect(() => {
    if (myAccount?.data && !isAccountSwitching) {
      setPreviousAccountData(myAccount.data);
    }
  }, [myAccount?.data, isAccountSwitching]);

  // Determine which account data to display (current or previous during switch)
  const displayAccountData = isAccountSwitching && !myAccount?.data
    ? previousAccountData
    : myAccount?.data;

  const raSpotifyId = displayAccountData?.spotifyid;
  const pdSpotifyId = (() => {
    try {
      return artistMeta?.data?.spotify_obj
        ? JSON.parse(artistMeta.data.spotify_obj)?.id
        : null;
    }
    catch (error) {
      console.warn("[AuthLayout] Invalid JSON in spotify_obj:", artistMeta?.data?.spotify_obj, error);
      return null;
    }
  })();

  const contracts = useGetContracts({
    artistaccountcode: selectedAccount,
  });

  useEffect(() => {
    if (siteConfig) {
      useAppStore.setState({
        allOpportunitiesAvailable: siteConfig?.all_opportunities_available ?? false,
        allSyncsAvailable: siteConfig?.all_syncs_available ?? false,
      });
    }
  }, [siteConfig]);

  useEffect(() => {
    if (contracts?.data?.results?.length > 0) {
      setShowContractModal(true);
    }
  }, [contracts]);

  useEffect(() => {
    setMenu(false);
  }, [location]);

  // takes the first 3 characters of the selected account code to get the tenant prefix
  // uses that as a parameter in the gettenant endpoint to get the tenant data object which contains currency ISO code
  // uses that to get the currency symbol from the currencies object
  // const tenantprefix = selectedAccount.length >= 3 ? selectedAccount.substring(0, 3) : '';
  const tenant = useQuery({
    queryKey: ["gettenant"],
    queryFn: () =>
      apiCall({ endpoint: "/rightsapp/get", path: "/api/v1/tenant/gettenant" }),
    enabled: !!selectedAccount,
    staleTime: 1000 * 60 * 60,
  });

  function getTenantCurrencySymbol(code) {
    if (code) {
      const matchingCurrency = currencies[code];

      if (matchingCurrency) {
        const currencySymbol = matchingCurrency.symbol;
        const locale = matchingCurrency.locale;

        useAppStore.setState({
          tenantCurrencyCode: code,
          tenantCurrencySymbol: currencySymbol,
          tenantCurrencyLocale: locale,
        });
      }
      else {
        console.error(`Currency data not found for code: ${code}`);
      }
    }
  }

  const currencyCode = tenant.data?.currencyIso4217Code;
  const showSecurityAlertBanner = [
    "sentricmusic.publishingdashboard.com",
    "uat.publishingdashboard.com",
  ].includes(window.location.hostname);
  const globalBanner = showSecurityAlertBanner
    ? {
        title: "Security Alert: Phishing Scam",
        messages: [
          "We're aware of a scam campaign targeting artists and managers with fake sync licensing opportunities.",
          "We will only contact you from @sentric.com, @sentricmusic.com or @believe.com, and we will never ask you for payment.",
          "If you receive a suspicious message, do not engage - mark it as spam and delete it.",
          "Please be assured that your data is safe. This phishing campaign is completely unconnected to our systems.",
        ],
      }
    : null;

  useEffect(() => {
    getTenantCurrencySymbol(currencyCode);
  }, [currencyCode]); // Empty dependency array means this effect runs once on mount

  useEffect(() => {
    if (userMeta.isSuccess && myAccounts.isSuccess) {
      if (newUser) {
        if (showOldSignUp) {
          navigate({ to: "/onboarding-1" });
        }
        else {
          navigate({ to: "/onboarding" });
        } // After confirming email, user is redirected to onboarding
        sendProperty({
          property: "sign_up_date",
          value: "CURRENT_DATE",
          email: profile?.data?.email,
        });

        sendEvent({ property: "pe14489857_sign_up_date" });

        sendProperty({
          property: "sign_up_domain",
          value: window.location.hostname,
          email: profile?.data?.email,
        });
      }
      if (noAccounts && !newUser) {
        navigate({ to: "/add-account-1" });
      }
    }
  }, [apiReady, userMeta, myAccounts, showOldSignUp, navigate, profile?.data?.email]);

  // use new end-point to update user address and name etc
  useEffect(() => {
    if (
      profile.isSuccess
      && userMeta.isSuccess
      && userMeta.data !== null
      && profile.data !== null
      && profile.data !== undefined
      && profile.data !== "undefined"
    ) {
      if (!profile.data.countrycode) {
        createProfileMutation.reset();
        const data = {
          firstname: profile.data.firstname,
          lastname: profile.data.lastname,
          addressline1: userMeta.data.address_1,
          addressline2: userMeta.data.address_2,
          addressline3: null,
          towncity: userMeta.data.town,
          countystate: userMeta.data.county,
          postzipcode: userMeta.data.postcode,
          phonenumber: null,
          countrycode: userMeta.data.country,
        };
        createProfileMutation.mutate(data, {
          onSuccess: () => {
            profile.refetch();
          },
          // onError: (error) => {
          //   console.log(error);
          // },
        });
      }
    }
  }, [profile.isSuccess, userMeta.isSuccess]);

  useEffect(() => {
    if (
      profile.isSuccess
      && profile.data !== undefined
      && profile.data !== "undefined"
      && profile.data !== null
    ) {
      if (!hubspotProps?.last_login_date) {
        sendProperty({
          property: "last_login_date",
          value: "CURRENT_DATE",
          email: profile.data.email,
        });
        useAppStore.setState({
          hubspot: { ...hubspotProps, last_login_date: true },
        });
      }
      sendEvent({ property: "pe14489857_portal_login" });
    }
  }, [profile.isSuccess, hubspotProps]);

  useEffect(() => {
    if (myAccounts?.data?.artistaccounts && myAccounts.data.artistaccounts.length > 0 && !selectedAccount) {
      const accounts = myAccounts.data.artistaccounts;

      // Check if user has a default account set in localStorage
      const defaultAccountCode = getDefaultAccount();
      const defaultAccount = defaultAccountCode
        ? accounts.find(acc => acc.artistaccountcode === defaultAccountCode)
        : null;

      if (defaultAccount) {
        // User has a valid default account - use it automatically
        useAppStore.setState({
          selectedAccount: defaultAccount.artistaccountcode,
          selectedCatalogue: defaultAccount.cataloguecode,
        });
      }
      else if (accounts.length === 1) {
        // Single account - select automatically
        const account = accounts[0];
        useAppStore.setState({
          selectedAccount: account.artistaccountcode,
          selectedCatalogue: account.cataloguecode,
        });
      }
      else if (accounts.length > 1) {
        // Multiple accounts, no default set - select first as fallback and show modal
        const account = accounts[0];
        useAppStore.setState({
          selectedAccount: account.artistaccountcode,
          selectedCatalogue: account.cataloguecode,
        });

        // Show the modal so user can choose a different account or set a default
        // BUT don't show if user is on the RaAuth page (delegated login in progress)
        if (!hasCheckedAccountOnLogin && !isOnRaAuthPage) {
          setShowLoginAccountModal(true);
          setHasCheckedAccountOnLogin(true);
          sessionStorage.setItem("@hasCheckedAccountOnLogin", "true");
        }
      }
    }
  }, [myAccounts, selectedAccount, hasCheckedAccountOnLogin]);

  useEffect(() => {
    const handleEvent = (event) => {
      const { message, data } = event;
      if (data === "close" || message === "close") {
        setShowContractModal(false);
        contracts.refetch();
      }
    };

    window.addEventListener("message", handleEvent, false);
    return function cleanup() {
      window.removeEventListener("message", handleEvent);
    };
  }, []);

  useEffect(() => {
    if (myAccount.isSuccess && profile.isSuccess) {
      sendProperty({
        property: "artist_name",
        value: displayAccountData?.accountname,
        email: profile?.data?.email,
      });
    }
  }, [myAccount.isSuccess, profile.isSuccess]);

  useEffect(() => {
    if (artistMeta.isSuccess && profile.isSuccess && artistMeta?.data) {
      // if (artistMeta?.data?.twitter_obj) {
      //   const tobj = JSON.parse(artistMeta.data.twitter_obj);
      //   sendProperty({
      //     property: 'twitterhandle',
      //     value: tobj?.data?.username,
      //     email: profile?.data?.email,
      //   });
      // }
      if (artistMeta?.data?.twitter) {
        sendProperty({
          property: "twitter",
          value: artistMeta?.data?.twitter_obj,
          email: profile?.data?.email,
        });
      }

      if (artistMeta?.data?.spotify_obj) {
        try {
          const sobj = JSON.parse(artistMeta.data.spotify_obj);
          sendProperty({
            property: "spotify_profile",
            value: sobj?.external_urls?.spotify,
            email: profile?.data?.email,
          });
        }
        catch (error) {
          console.warn("[AuthLayout] Invalid JSON in spotify_obj for analytics:", artistMeta?.data?.spotify_obj, error);
        }
      }

      if (artistMeta?.data?.songkick_obj) {
        try {
          const skobj = JSON.parse(artistMeta.data.songkick_obj);
          sendProperty({
            property: "songkick_profile",
            value: skobj?.uri,
            email: profile?.data?.email,
          });
        }
        catch (error) {
          console.warn("[AuthLayout] Invalid JSON in songkick_obj for analytics:", artistMeta?.data?.songkick_obj, error);
        }
      }

      if (artistMeta?.data?.facebook_obj) {
        sendProperty({
          property: "facebook_page_url",
          value: artistMeta?.data?.facebook_obj,
          email: profile?.data?.email,
        });
      }
      if (artistMeta?.data?.instagram_obj) {
        sendProperty({
          property: "instagram",
          value: artistMeta?.data?.instagram_obj,
          email: profile?.data?.email,
        });
      }
    }
  }, [artistMeta.isSuccess, profile.isSuccess]);

  useEffect(() => {
    if (
      artistMeta.isSuccess
      && myAccount.isSuccess
      && artistMeta?.data
      && displayAccountData
    ) {
      async function checkSpotify() {
        if (!artistMeta.isSuccess || !myAccount.isSuccess || !artistMeta?.data || !myAccount?.data) {
          return;
        }

        const baseData = {
          account_id: selectedAccount,
          facebook_obj: artistMeta.data?.facebook,
          songkick_obj: artistMeta.data?.facebook?.songkick,
          twitter_obj: artistMeta.data?.facebook?.twitter,
          bandsintown_obj: artistMeta.data?.facebook?.bandsintown,
          instagram_obj: artistMeta.data?.facebook?.instagram,
        };

        const updateMeta = (spotify_obj) => {
          updateArtistMeta.reset();
          updateArtistMeta.mutate({ ...baseData, spotify_obj }, {
            onSuccess: () => artistMeta.refetch(),
          });
        };

        // Clear Spotify if mismatch
        if (!raSpotifyId && pdSpotifyId) {
          console.log("Doesnt Match!!");
          updateMeta(null);
          return;
        }

        // Fetch and set Spotify data
        if (raSpotifyId && !pdSpotifyId) {
          try {
            const responseJson = await apiCall({
              endpoint: "/rightsapp/get",
              path: `/api/v1/spotify/artist/${raSpotifyId}`,
            });
            const dto = responseJson?.artist;
            if (!dto?.id) {
              throw new Error("Unexpected artist response shape");
            }
            // Map to legacy snake_case shape expected by all spotify_obj consumers
            const artist = {
              id: dto.id,
              name: dto.name,
              images: dto.images ?? [],
              ...(dto.spotifyUrl != null && { external_urls: { spotify: dto.spotifyUrl } }),
            };
            updateMeta(JSON.stringify(artist));
          }
          catch {
            console.error("Error fetching Spotify artist data");
          }
        }
      }
      checkSpotify();
    }
  }, [artistMeta.isSuccess, myAccount.isSuccess]);

  useEffect(() => {
    if (configIsSuccess) {
      document.title = `${siteConfig?.display_name} Portal`;
    }
  }, [configIsSuccess]);

  if (!myAccounts?.data || !configIsSuccess) {
    return <Loading />;
  }

  const { showKyc, showKycPending } = processKycStatuses(kyc);
  const hideRoyalties = profile?.data?.hideroyalties || showKyc
    || showKycPending
    || displayAccountData?.hideroyalties;
  return (
    <div className={authLayoutClasses}>
      <div className="flex flex-row flex-wrap flex-grow min-h-screen lg:flex-nowrap">
        <div
          style={
            isTunecore
              ? { backgroundColor: "#141414" }
              : { backgroundColor: darkColour }
          }
          className="fixed z-30 flex-shrink-0 w-full text-white lg:w-60 lg:relative lg:min-w-[15rem] lg:max-w-[15rem]"
        >
          <Sidebar
            darkColour={darkColour}
            highlightColour={highlightColour}
            siteConfig={siteConfig}
            displayAccountData={displayAccountData}
            hideRoyalties={hideRoyalties}
            isTunecore={isTunecore}
            isDesktop
            showKyc={showKyc}
            showKycPending={showKycPending}
            accounts={{ artistaccounts: filteredAccounts }}
            selectedAccount={selectedAccount}
            isAccountSwitching={isAccountSwitching}
          />
        </div>

        <div className="w-full mt-20 lg:mt-0 lg:w-[calc(100%-15rem)] lg:max-w-[calc(100%-15rem)]">
          <div
            style={
              isTunecore
                ? { backgroundColor: "#141414" }
                : { backgroundColor: "#FFFFFF" }
            }
            className={`${isTunecore ? "text-white border-b border-tunecore-light-grey" : "text-black"} ${
              showKyc || showKycPending ? "h-32 lg:h-16" : "h-16"
            } flex flex-col lg:flex-row items-center justify-between lg:justify-end fixed w-full lg:w-full-minus-nav z-30`}
          >
            <div className="flex flex-row justify-between order-1 w-full h-16 px-4 lg:justify-end sm:order-2 lg:w-max">
              {width < 1024 && ( // lg breakpoint
                <div className="w-full">
                  <button
                    onClick={() => setMenu(!menu)}
                    type="button"
                    className="flex items-center justify-center h-full hover:underline"
                  >
                    <FaBars className="mr-2" />
                    {" "}
                    <p className="mt-1">Menu</p>
                  </button>
                </div>
              )}
              <div className="flex items-center self-center gap-3">

                {!isProduction && !siteConfig?.demo_mode && (
                  <DevTools
                    setIsAccountSwitching={setAccountSwitching}
                    setPreviousAccountData={setPreviousAccountData}
                    FORCE_TUNECORE_MODE={FORCE_TUNECORE_MODE}
                    setForceTunecoreMode={setForceTunecoreMode}
                    selectedAccount={selectedAccount}
                    myAccounts={{ data: { artistaccounts: filteredAccounts } }}
                  />
                )}
                <LanguageSelector />
              </div>

              {displayAccountData?.accountname && (
                <>
                  <UserDropDown
                    highlightColour={highlightColour}
                    siteConfig={siteConfig}
                    accounts={{ artistaccounts: filteredAccounts }}
                    logout={logout}
                    data={displayAccountData}
                    selectedAccount={selectedAccount}
                    isAccountSwitching={isAccountSwitching}
                  />
                </>
              )}
            </div>

            {showKyc && (
              <a
                href="/security"
                type="link"
                className="flex flex-col items-center justify-center w-full h-16 p-4 text-xs font-semibold text-center text-black bg-red-500 lg:grow sm:text-sm lg:text-base hover:underline"
              >
                <p href="/security" className="flex items-center gap-3">
                  <IoWarningOutline className="hidden lg:inline" />
                  {t("common:kyc-warning")}
                </p>
              </a>
            )}
            {showKycPending && (
              <a className="flex flex-col items-center justify-center w-full h-16 p-4 text-xs font-semibold text-center text-black bg-yellow-500 lg:w-grow sm:text-sm lg:text-base ">
                <p className="flex items-center gap-3">
                  <IoWarningOutline className="hidden lg:inline" />
                  {t("common:kyc-pending-warning")}
                </p>
              </a>
            )}
          </div>

          {menu && ( // mobile version
            <div
              className={`${
                showKyc || showKycPending ? "top-52 lg:top-36" : "top-36"
              } z-50 fixed w-full lg:hidden`}
              style={
                isTunecore
                  ? { backgroundColor: "#141414" }
                  : { backgroundColor: darkColour }
              }
            >
              <Sidebar
                darkColour={darkColour}
                highlightColour={highlightColour}
                siteConfig={siteConfig}
                displayAccountData={displayAccountData}
                hideRoyalties={hideRoyalties}
                isTunecore={isTunecore}
                isDesktop={false}
                showKyc={showKyc}
                showKycPending={showKycPending}
                accounts={{ artistaccounts: filteredAccounts }}
                selectedAccount={selectedAccount}
                isAccountSwitching={isAccountSwitching}
              />
            </div>
          )}

          <div
            className={`${
              showKyc || showKycPending ? "mt-32 lg:mt-16" : "mt-16"
            } lg:pb-0 w-full max-w-full overflow-x-hidden`}
          >
            {globalBanner && (
              <AnnouncementBanner
                level="error"
                title={globalBanner.title}
                messages={globalBanner.messages}
              />
            )}
            {children}
          </div>

          {/* Account switching overlay */}
          {isAccountSwitching && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="p-8 bg-white rounded-lg shadow-lg">
                <div className="flex flex-col items-center space-y-4">
                  <div id="bars">
                    <div className="bar" />
                    <div className="bar" />
                    <div className="bar" />
                    <div className="bar" />
                    <div className="bar" />
                    <div className="bar" />
                    <div className="bar" />
                    <div className="bar" />
                    <div className="bar" />
                    <div className="bar" />
                  </div>
                  <p className="text-lg font-medium text-gray-700">Switching account...</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {showContractModal && contracts?.data?.results?.length && (
        <div className="fixed top-0 left-0 z-50 flex justify-center w-full h-full bg-black">
          <div className="w-full h-full bg-white">
            <iframe
              width="100%"
              height="100%"
              title="sign contract"
              src={`https://contracts-uat.sentricapp.com/sign/${contracts.data.results[0]?.code}?exit=1`}
              frameBorder="0"
              style={{ height: "100%" }}
            />
          </div>
        </div>
      )}

      {/* Login account selection modal - shown on login for users with multiple accounts */}
      {showLoginAccountModal && filteredAccounts.length > 1 && (
        <ChooseAccountModal
          accounts={filteredAccounts}
          title={t("auth:select-account-to-continue", "Please select which account you'd like to use:")}
          to={location.pathname || "/"}
          setAccountChoiceModal={setShowLoginAccountModal}
          open={showLoginAccountModal}
          isLoginFlow={true}
        />
      )}
    </div>
  );
}
export default AuthLayout;
